/**
 * A date the user TYPES, day first (`31. 12. 2026`), plus a calendar button
 * that opens the browser's own picker — the CRM's `createDateInput` pattern
 * without the CRM's open-ended value (#646).
 *
 * The text box is the field: it carries the id, the label, the error and the
 * focus. The native `<input type="date">` stays rendered but out of sight,
 * because `showPicker()` refuses a `display: none` control; whatever it
 * returns is rewritten day-first into the text box.
 */
import { formatDayFirst, parseDayFirstDate } from '../../shared/utils/dayFirstDate.js';

export interface DayFirstDateInput {
  /** The wrapper: text box + calendar button + the hidden native picker. */
  readonly node: HTMLElement;
  /** The text box — what the label, the error and the focus point at. */
  readonly input: HTMLInputElement;
  /** `'YYYY-MM-DD'` when the text parses, `null` when blank or unparsable. */
  iso(): string | null;
  /** Why the text does not parse, or `null` when it is blank or fine. */
  problem(): string | null;
  /** Show an ISO date in the text box. */
  setIso(iso: string | null): void;
}

export function createDayFirstDateInput(opts: { id?: string; className?: string } = {}): DayFirstDateInput {
  const wrap = document.createElement('span');
  wrap.className = opts.className ?? 'dfd';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'dfd-text';
  if (opts.id) input.id = opts.id;
  // An example, marked as one — a bare date reads as a value already in the box.
  input.placeholder = 'npr. 31. 12. 2026';
  input.autocomplete = 'off';
  input.spellcheck = false;
  // The normal text keyboard on purpose: a numeric keypad has no dot or slash.

  const native = document.createElement('input');
  native.type = 'date';
  native.className = 'dfd-native';
  native.tabIndex = -1;
  native.setAttribute('aria-hidden', 'true');

  const pick = document.createElement('button');
  pick.type = 'button';
  pick.className = 'dfd-pick';
  pick.textContent = '📅';
  pick.setAttribute('aria-label', 'Izberi datum v koledarju');
  pick.title = 'Koledar';

  const parsed = (): { value: string | null } | { error: string } => parseDayFirstDate(input.value);
  const iso = (): string | null => {
    const p = parsed();
    return 'value' in p ? p.value : null;
  };
  const problem = (): string | null => {
    const p = parsed();
    return 'error' in p ? p.error : null;
  };
  const setIso = (value: string | null): void => {
    input.value = formatDayFirst(value);
  };

  pick.addEventListener('click', () => {
    native.value = iso() ?? '';
    try {
      // The click IS the user gesture the picker needs.
      native.showPicker();
    } catch {
      native.click();
    }
  });
  native.addEventListener('change', () => {
    setIso(native.value === '' ? null : native.value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  });

  wrap.append(input, pick, native);
  return { node: wrap, input, iso, problem, setIso };
}
